import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FiCheck, FiRefreshCw, FiMessageSquare, FiThumbsUp, FiThumbsDown, FiDatabase } from 'react-icons/fi';
import FenrisLogo from '../../assets/images/fenris.svg';
import { useToast } from '../../context/ToastContext';
import {
  CommercialFieldValues,
  FIELD_DEFS,
  computeDefaultToggles,
  buildUpdateInsuredPayload,
  buildUpdateClRatingPayload,
  buildHistoryUpdatePayload,
  updateInsuredDetails,
  updateInsuredClRatingDetails,
  updateFenrisHistoryRecord,
  submitLikeDislike,
  submitFenrisFeedback,
} from './dataEnrichmentCommercialApi';

interface MatchCandidate {
  name: string;
  [key: string]: any;
}

interface Props {
  insuredId: string;
  description: string;
  existing: CommercialFieldValues;
  fenris: CommercialFieldValues;
  matches?: MatchCandidate[];
  onSelectMatch?: (index: number) => void;
  transactionId: string;
  showFeedback?: boolean;
  onUpdated?: () => void;
}

const DataEnrichmentCommercialCompare: React.FC<Props> = ({
  insuredId, description, existing, fenris, matches, onSelectMatch,
  transactionId, showFeedback = true, onUpdated,
}) => {
  const [toggles, setToggles] = useState<Record<string, boolean>>(() => computeDefaultToggles(existing));
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateDone, setUpdateDone] = useState(false);

  const { showSuccess, showError } = useToast();

  /* Feedback popover */
  const feedbackBtnRef = useRef<HTMLButtonElement>(null);
  const feedbackPanelRef = useRef<HTMLDivElement>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackAnchor, setFeedbackAnchor] = useState<{ bottom: number; right: number } | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackError, setFeedbackError] = useState('');
  const [thumbsUp, setThumbsUp] = useState(false);
  const [thumbsDown, setThumbsDown] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackDone, setFeedbackDone] = useState(false);

  useEffect(() => {
    setToggles(computeDefaultToggles(existing));
    setUpdateDone(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuredId, transactionId]);

  const toggleField = (key: string) => setToggles((prev) => ({ ...prev, [key]: !prev[key] }));

  const onUpdate = useCallback(async () => {
    const hasActive = Object.values(toggles).some(Boolean);
    if (!hasActive) {
      showError('Please enable at least one toggle button.');
      return;
    }
    setUpdateLoading(true);
    try {
      await updateInsuredDetails(buildUpdateInsuredPayload(insuredId, existing, fenris, toggles));
      await updateInsuredClRatingDetails(buildUpdateClRatingPayload(insuredId, existing, fenris, toggles, description));
      await updateFenrisHistoryRecord(buildHistoryUpdatePayload(fenris, transactionId));
      showSuccess('Data updated in Momentum.');
      setUpdateDone(true);
      onUpdated?.();
    } catch (err: any) {
      showError(err?.response?.data?.errorMessage || 'Error while updating insured details.');
    } finally {
      setUpdateLoading(false);
    }
  }, [toggles, insuredId, existing, fenris, description, transactionId, onUpdated]);

  const openFeedback = () => {
    const rect = feedbackBtnRef.current?.getBoundingClientRect();
    if (rect) setFeedbackAnchor({ bottom: window.innerHeight - rect.top + 8, right: window.innerWidth - rect.right });
    setFeedbackText('');
    setFeedbackError('');
    setFeedbackOpen(true);
  };
  const closeFeedback = () => { setFeedbackOpen(false); setFeedbackAnchor(null); };

  useEffect(() => {
    if (!feedbackOpen) return;
    const handler = (e: MouseEvent) => {
      if (feedbackPanelRef.current && !feedbackPanelRef.current.contains(e.target as Node)
        && feedbackBtnRef.current && !feedbackBtnRef.current.contains(e.target as Node)) {
        closeFeedback();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackOpen]);

  const onThumbs = (like: boolean) => {
    setThumbsUp(like);
    setThumbsDown(!like);
    submitLikeDislike(transactionId, like, !like).then(() => {
      setFeedbackDone(true);
      showSuccess('Feedback submitted successfully.');
      setTimeout(closeFeedback, 600);
    }).catch(() => showError('Unknown error while inserting likedislike.'));
  };

  const onSubmitFeedback = () => {
    if (!feedbackText.trim()) { setFeedbackError('Please provide your feedback'); return; }
    if (feedbackText.length > 250) { setFeedbackError('Please provide your feedback within 250 characters'); return; }
    setFeedbackSubmitting(true);
    submitFenrisFeedback(transactionId, feedbackText)
      .then(() => {
        showSuccess('Feedback submitted successfully.');
        setFeedbackSubmitting(false);
        setTimeout(closeFeedback, 800);
      })
      .catch(() => {
        showError('Unknown error while inserting feedback.');
        setFeedbackSubmitting(false);
      });
  };

  return (
    <div className="dec-compare-wrap">
      <div className="dec-panels">
        {/* ── Existing Data (AMS) ── */}
        <div className="dec-panel dec-panel--existing">
          <div className="dec-panel__header">
            <span className="dec-panel__header-icon dec-panel__header-icon--existing"><FiDatabase size={16} /></span>
            <span className="dec-panel__header-title">Existing Data</span>
          </div>
          <div className="dec-panel__body">
            {FIELD_DEFS.map((f) => (
              <div key={f.key} className="dec-field-row">
                <span className="dec-field-row__label">{f.label}</span>
                <span className="dec-field-row__value">{existing[f.key] || '—'}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Fenris Data ── */}
        <div className="dec-panel dec-panel--fenris">
          <div className="dec-panel__header">
            <img src={FenrisLogo} alt="Fenris" className="dec-panel__header-icon" />
            <span className="dec-panel__header-title">Fenris Data</span>
            {matches && matches.length > 1 && (
              <select
                className="dec-panel__match-select"
                onChange={(e) => onSelectMatch?.(parseInt(e.target.value, 10))}
                defaultValue={0}
              >
                {matches.map((m, i) => (
                  <option key={i} value={i}>{m.name}</option>
                ))}
              </select>
            )}
            <span className="dec-panel__use-label">Use This Value</span>
          </div>
          <div className="dec-panel__body">
            {FIELD_DEFS.map((f) => {
              const fenrisEmpty = !fenris[f.key] || !fenris[f.key].trim();
              return (
                <div key={f.key} className="dec-field-row dec-field-row--fenris">
                  <span className="dec-field-row__label">{f.label}</span>
                  <span className="dec-field-row__value">{fenris[f.key] || '—'}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!toggles[f.key]}
                    className={`dec-toggle${toggles[f.key] ? ' dec-toggle--on' : ''}`}
                    disabled={fenrisEmpty}
                    onClick={() => toggleField(f.key)}
                  >
                    <span className="dec-toggle__knob" />
                  </button>
                </div>
              );
            })}
          </div>
          <div className="dec-panel__footer">
            {showFeedback && !updateDone && (
              <button ref={feedbackBtnRef} className="cs-browse-btn" onClick={openFeedback} disabled={feedbackDone}>
                <FiMessageSquare size={13} />
                Feedback
              </button>
            )}
            {updateDone ? (
              <span className="ho-badge ho-badge--success"><FiCheck size={11} /> Data Updated in Momentum</span>
            ) : updateLoading ? (
              <span className="dec-inline-loader"><FiRefreshCw size={13} className="ho-spin" /> Updating…</span>
            ) : (
              <button className="cs-browse-btn" onClick={onUpdate}>
                Update the record in Momentum
              </button>
            )}
          </div>
        </div>
      </div>

      {feedbackOpen && feedbackAnchor && ReactDOM.createPortal(
        <div ref={feedbackPanelRef} className="csfb-popup" style={{ bottom: feedbackAnchor.bottom, right: feedbackAnchor.right }}>
          <h4 className="csfb-title">Feedback</h4>
          <textarea
            className="csfb-textarea"
            placeholder="Enter your feedback here"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            rows={4}
          />
          {feedbackError && <span style={{ fontSize: 11, color: 'var(--accent-red, #ef4444)', marginTop: 4, display: 'block' }}>{feedbackError}</span>}
          <div className="csfb-rating">
            <button className={`csfb-rating-btn${thumbsUp ? ' csfb-rating-btn--up' : ''}`} onClick={() => onThumbs(true)} title="Thumbs up"><FiThumbsUp size={20} /></button>
            <button className={`csfb-rating-btn${thumbsDown ? ' csfb-rating-btn--down' : ''}`} onClick={() => onThumbs(false)} title="Thumbs down"><FiThumbsDown size={20} /></button>
          </div>
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
            <button className="csfb-btn-submit" disabled={feedbackSubmitting} onClick={onSubmitFeedback}>
              {feedbackSubmitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default DataEnrichmentCommercialCompare;
